<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import PostDataService from '@/services/PostDataService'
import PostCategoryDataService, { type PostCategory } from '@/services/PostCategoryDataService'
import type Post from '@/types/Post'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import * as _ from 'lodash'

export default defineComponent({
  name: 'post-list', data() {
    return {
      posts: [] as Post[],
      currentPost: {} as Post,
      router: useRouter(),
      filterPosts: '',
      loaded: false,
      pagination: {} as any,
      postCategories: [] as PostCategory[],
      showDeleteModal: false,
      postToDelete: null as Post | null
    }
  }, methods: {
    async loadCategories() {
      try {
        const response = await PostCategoryDataService.getAll()
        this.postCategories = (response.data as any)?.data || response.data
      } catch (error: unknown) {
        console.error('Error loading categories:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
      }
    },

    getCategoryName(categoryId: number | undefined): string {
      if (!categoryId) return 'Nezaradené'
      const category = this.postCategories.find(cat => cat.id === categoryId)
      return category ? category.name : `ID: ${categoryId}`
    },

    retrievePosts(page: number, type: string) {
      if (type == 'prev') {
        page--;
      } else if (type == 'next') {
        page++;
      }

      PostDataService.getAllPosts(page)
        .then((response: ResponseData) => {
          this.posts = response.data.data.data
          this.pagination = response.data.data
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
          this.loaded = true
        })
    },
    refreshList() {
      this.retrievePosts(1, '')
      this.currentPost = {} as Post
    }, addPost() {
      this.router.push({
        name: 'Pridať článok',
      })
    }, stripHtml(content: string): string {
      // Remove HTML tags and markdown formatting for display
      return content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/[#*_`]/g, '') // Remove markdown formatting
        .replace(/\n/g, ' ') // Replace newlines with spaces
        .trim()
        .substring(0, 100) + (content.length > 100 ? '...' : '') // Truncate
    }, async confirmDelete(post: Post) {
      if (!post.id) {
        toast.error('Chyba: ID článku nie je definované')
        return
      }
      this.postToDelete = post
      this.showDeleteModal = true
    },

    async deletePost() {
      if (!this.postToDelete?.id) {
        toast.error('Chyba: ID článku nie je definované')
        return
      }

      try {
        await PostDataService.delete(this.postToDelete.id)
        toast.success('Článok bol úspešne zmazaný')
        this.showDeleteModal = false
        this.postToDelete = null
        this.refreshList()
      } catch (error: unknown) {
        console.error('Error deleting post:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri mazaní článku: ' + errorMessage)
      }
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.postToDelete = null
    },
  },
  computed: {
    filteredPosts() {
      return this.posts.filter(post => {
        const title = post.title.toLowerCase()
        const category_id = post.category_id?.toString() || ''
        const searchTerm = this.filterPosts.toLowerCase()

        return title.includes(searchTerm) || category_id.includes(searchTerm)
      })
    },
  }, mounted() {
    Promise.all([
      this.retrievePosts(1, ''),
      this.loadCategories()
    ])
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <h1 class="text-2xl font-semibold text-gray-900">Zoznam článkov</h1>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div class="py-4">
          <div class="flex flex-col">
            <div class="overflow-x-auto">
              <div class="flex flex-row justify-between">
                <div class="relative max-w-xs">
                  <label for="search" class="sr-only"> Hľadať </label>
                  <FormKit v-model="filterPosts" type="search" name="search"
                    class="block w-full p-3 pl-10 text-sm border-gray-200 rounded-md focus:border-teal-500 focus:ring-teal-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Hľadať..." />
                </div>
                <div class="basis-1/4">
                  <button type="button" @click="addPost()"
                    class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                    Pridať článok
                  </button>
                </div>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="!loaded">
                <div class="text-center">
                  <div role="status">
                    <svg class="inline mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-teal-600"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                    </svg>
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="loaded && !posts.length">
                <p>No items found</p>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="loaded && posts.length">
                <div class="overflow-hidden border rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col"
                          class="flex items-center px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          ID
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"> Nadpis </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"> Úryvok </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"> Kategória </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-center text-gray-500 uppercase">Akcie
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr class="hover:bg-gray-200" v-for="post in filteredPosts" :key="post.id">
                        <td class="px-6 py-4 text-sm font-medium text-gray-800">{{ post.id }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ post.title }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          <div class="max-w-xs truncate" :title="post.content">
                            {{ stripHtml(post.content) }}
                          </div>
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          {{ getCategoryName(post.category_id) }}
                        </td>
                        <td class="px-6 py-4 text-sm font-medium">
                          <div class="flex items-center justify-center space-x-2">
                            <router-link :to="'/post-detail/' + post.id"
                              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Upraviť
                            </router-link>
                            <button @click="confirmDelete(post)"
                              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Zmazať
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div class="flex flex-1 justify-between sm:hidden">
                      <a href="#"
                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Previous</a>
                      <a href="#"
                        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Next</a>
                    </div>
                    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p class="text-sm text-gray-700">
                          Stránka
                          <span class="font-medium">{{ pagination.current_page }}</span>
                          z
                          <span class="font-medium">{{ pagination.last_page }}</span>
                        </p>
                      </div>
                      <div>
                        <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.currentPage <= 1"
                            @click="retrievePosts(pagination.current_page, 'prev')">
                            <span class="sr-only">Previous</span>
                            <!-- Heroicon name: mini/chevron-left -->
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                          <button v-for="item in pagination.last_page" :key="item" @click="retrievePosts(item, '')"
                            class="relative inline-flex items-center border text-gray-500 hover:bg-gray-50 bg-white px-4 py-2 text-sm font-medium border-gray-300"
                            :class="item == pagination.currentPage
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'border-gray-300 hover:bg-gray-50 focus:z-20'
                              ">
                            {{ item }}
                          </button>
                          <button
                            class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.currentPage >= pagination.last_page"
                            @click="retrievePosts(pagination.current_page, 'next')">
                            <span class="sr-only">Next</span>
                            <!-- Heroicon name: mini/chevron-right -->
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="cancelDelete">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <div class="mt-3 text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg leading-6 font-medium text-gray-900 mt-4">Potvrdiť vymazanie</h3>
          <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-500">
              Naozaj chcete zmazať článok <strong>"{{ postToDelete?.title }}"</strong>?<br>
              Táto akcia sa nedá vrátiť.
            </p>
          </div>
          <div class="items-center px-4 py-3">
            <div class="flex space-x-3">
              <button @click="cancelDelete"
                class="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300">
                Zrušiť
              </button>
              <button @click="deletePost"
                class="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300">
                Zmazať
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
